import React, { useState, useEffect } from "react";
import * as authActions from "../../../../../AxiosRtk/Auth";
import { AlertModal } from "../../../../../../../setup/alert/swalAlert";
import { alertIconType } from "../../../../../../../setup/alert/iconTypes";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useLangTranslation from "../../../../../../customHooks/languageProvider";
import AuthTermsService from "../../../../../../shared/Components/AuthTermsService";
import AuthSignUp from "../../../../../../shared/Components/AuthSignUp";
import LoadingIndicator from "../../../../../../../setup/Loader/LoaderIndicator";
import { setShowMessage } from "../../../../../AxiosRtk/successMessage";
import useCrypto from "../../../../../../customHooks/crypto";

const VerificationCode = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [timer, setTimer] = useState(119);
  const [loader, setLoader] = useState(false);
  const { getLangTranslation } = useLangTranslation();
  const [pinCode, setPincode] = useState({
    num1: "",
    num2: "",
    num3: "",
    num4: "",
  });
  const { verificationCodeSubmit, verificationCode, setForgotPwdToken } =
    authActions;
  const { encryptRequest } = useCrypto();
  const { forgotPwd } = useSelector(({ auth }) => auth);

  useEffect(() => {
    if (forgotPwd?.forgotPwdToken === null) {
      history.push("/auth/forgot-password");
    }
  }, [forgotPwd, history]);

  // Handle OTP input change and move focus to the next or previous field
  function handleInputChange(e) {
    const { name, value } = e.target;
    const inputIndex = parseInt(name.charAt(name.length - 1));
    const nextInput = document.querySelector(`input[name=num${inputIndex + 1}]`);
    const prevInput = document.querySelector(`input[name=num${inputIndex - 1}]`);

    // Single digit input handling
    setPincode((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // If value is entered, move focus to the next input
    if (value.length === 1 && nextInput) {
      nextInput.focus();
    }

    // If backspace and empty input, move focus to the previous input
    if (value === "" && prevInput) {
      prevInput.focus();
    }
  }

  // Handle the OTP paste event
  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text");
    if (paste.length === 4) {
      const valueArray = paste.split("");

      // Set the values for each input box
      setPincode({
        num1: valueArray[0] || "",
        num2: valueArray[1] || "",
        num3: valueArray[2] || "",
        num4: valueArray[3] || "",
      });

      // Focus the last input after pasting
      document.querySelector("input[name=num4]").focus();
    }
    e.preventDefault(); // Prevent default paste behavior
  };

  function handleVerifySubmit() {
    let pinStr = Object.values(pinCode).join("");
    if (pinStr.length !== 4) {
      AlertModal(
        "Pincode must be 4 digits number",
        alertIconType.errorIcon,
        true
      );
      return;
    }
    setLoader(true);
    const encryptedVerifyRequest = encryptRequest(
      JSON.stringify({ email: forgotPwd?.forgotPwdEmail, otp: parseInt(pinStr) })
    )?.content;

    verificationCodeSubmit(encryptedVerifyRequest)
      .then((res) => {
        dispatch(
          setShowMessage({
            showMessage: true,
            defaultMessage: res?.data?.message,
            message: res?.data?.message,
          })
        );
        setTimeout(() => {
          history.push("/auth/create-new-password");
        }, 3000);
      })
      .catch((err) => {
        AlertModal(err?.response?.data?.message, alertIconType.errorIcon, true);
      })
      .finally(() => {
        setLoader(false);
      });
  }

  useEffect(() => {
    const timerInterval = setInterval(() => {
      setTimer((previousTime) => {
        if (previousTime === 0) {
          clearInterval(timerInterval);
          return 0;
        } else {
          return previousTime - 1;
        }
      });
    }, 1000);
    return () => clearInterval(timerInterval);
  }, []);

  function getFormattedTime() {
    const min = Math.floor(timer / 60);
    const sec = timer % 60;

    return `0${min}:${sec > 9 ? sec : `0${sec}`}`;
  }

  function sendOtpAgain() {
    setLoader(true);
    verificationCode(forgotPwd?.forgotPwdEmail)
      .then((res) => {
        dispatch(
          setShowMessage({
            showMessage: true,
            defaultMessage: res?.data?.message,
            message: res?.data?.message,
          })
        );
        dispatch(
          setForgotPwdToken({
            forgotPwdToken: res?.data?.result[0]?.token,
            forgotPwdEmail: forgotPwd?.forgotPwdEmail,
          })
        );
        setTimer(119);
      })
      .catch((err) => {
        AlertModal(
          getLangTranslation(err.response.data.message),
          alertIconType.errorIcon
        );
      })
      .finally(() => {
        setLoader(false);
      });
  }

  return (
    <div className="container">
      <div className="auth-wrapper">
        <div className="auth-inner">
          <h2 className="text-center mb-3">
            {getLangTranslation("ENTER_VERIFICATION_CODE", "ENTER VERIFICATION CODE")}
          </h2>
          <p className="text-center mb-6 light-text">
            {getLangTranslation(
              "VERIFICATION_CODE_SENT_TO_EMAIL",
              "A code has been sent to your E-mail address"
            )}
          </p>

          {timer !== 0 ? (
            <p className="text-center mb-6 light-text">
              {getLangTranslation(
                "VERIFICATION_CODE_SEND_AGAIN_IN",
                "Send again in"
              )}{" "}
              {getFormattedTime()}
            </p>
          ) : loader ? (
            <LoadingIndicator height={10} width={50} color="#66B5FF" />
          ) : (
            <p
              className="text-capitalize c-pointer text-center"
              onClick={() => sendOtpAgain()}
            >
              <u>{getLangTranslation("SEND_OTP_AGAIN", "Send otp again")}</u>
            </p>
          )}

          <div className="verification-code-wrap">
            <div className="mb-6 grid" style={{ gridGap: ".75rem" }}>
              {["num1", "num2", "num3", "num4"].map((name, index) => (
                <input
                  key={index}
                  type="text"
                  className="form-control g-col-3"
                  name={name}
                  maxLength="1"
                  value={pinCode[name]}
                  onChange={handleInputChange}
                  onPaste={handlePaste} // Handle paste event
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && pinCode[name] === "") {
                      const prevInput = document.querySelector(
                        `input[name=num${index}]`
                      );
                      if (prevInput) prevInput.focus();
                    }
                  }}
                />
              ))}
            </div>
            <div className="d-grid">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleVerifySubmit}
                disabled={loader}
              >
                <span className="btn-upper-case">
                  {getLangTranslation("VERIFICATION_CODE_BTN_TXT", "SEND")}
                </span>
              </button>
            </div>
          </div>

          <AuthSignUp />
          <AuthTermsService />
        </div>
      </div>
    </div>
  );
};

export default VerificationCode;
